import React, {useCallback, useEffect, useMemo, useState} from "react";
import {WithdrawalMinimum} from "../../components/WithdrawalMinimum";
import {
    addTeamPayoutAddress,
    adminSignUp,
    applyTeamBalanceOperation,
    cancelTeamWithdrawal,
    completeTeamWithdrawal,
    createTeam,
    disableTeamPayoutAddress,
    formatTeamWithdrawalError,
    listMerchants,
    listPendingTeamWithdrawals,
    listTeamPayoutAddresses,
    listTeams,
    listTraders,
    resetTeamBalanceAsAdmin,
} from "../../api";
import type {
    MerchantResponse,
    TeamBalanceOperationType,
    TeamItem,
    TeamPayoutAddress,
    TeamWithdrawalPendingItem,
    TraderItem,
} from "../../api";
import {useAuth} from "../../auth";
import {AdminSecondFactorSection, ErrorBanner, Modal, SecondFactorControl} from "../../components";
import {
    generateMerchantPortalPassword,
    validateCredentialLogin,
    validateCredentialPassword,
} from "../../utils";
import {isPositiveAmountString, newOperationKey, validateUsdtAmount} from "./merchant-details/cashFlow";

const LIST_LIMIT = 200;
const TEAM_NAME_MAX = 50;

function shortId(id: string): string {
    if (id.length <= 12) return id;
    return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

/** Полный UUID в узкой колонке ломался по символу: показываем короткий, полный — в подсказке и по клику в буфер. */
function CopyableId({id}: {id: string}): JSX.Element {
    const [copied, setCopied] = useState(false);
    const copy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };
    return (
        <button type="button" className="id-copy mono" title={`${id} — нажмите, чтобы скопировать`} onClick={() => void copy()}>
            {copied ? "скопировано" : shortId(id)}
        </button>
    );
}

function matchesTeam(team: TeamItem, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return team.name.toLowerCase().includes(q) || team.id.toLowerCase().includes(q);
}

function matchesTrader(trader: TraderItem, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
        trader.login.toLowerCase().includes(q) ||
        trader.name.toLowerCase().includes(q) ||
        (trader.teamName || "").toLowerCase().includes(q) ||
        trader.id.toLowerCase().includes(q)
    );
}

export function AdminTradersPage(): JSX.Element {
    const {token} = useAuth();

    const [teams, setTeams] = useState<TeamItem[]>([]);
    const [traders, setTraders] = useState<TraderItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);
    const [success, setSuccess] = useState("");
    const [teamQuery, setTeamQuery] = useState("");
    const [traderQuery, setTraderQuery] = useState("");

    const [teamOpen, setTeamOpen] = useState(false);
    const [teamName, setTeamName] = useState("");
    const [teamSaving, setTeamSaving] = useState(false);
    const [teamError, setTeamError] = useState<unknown>(null);

    const [traderOpen, setTraderOpen] = useState(false);
    const [traderLogin, setTraderLogin] = useState("");
    const [traderPassword, setTraderPassword] = useState("");
    const [traderName, setTraderName] = useState("");
    const [traderTeamId, setTraderTeamId] = useState("");
    const [traderPasswordVisible, setTraderPasswordVisible] = useState(false);
    const [traderSaving, setTraderSaving] = useState(false);
    const [traderError, setTraderError] = useState<unknown>(null);

    // Вывод балансов командам: рабочий экран «кому и сколько отправлять» и
    // адреса вывода в карточке команды. Деньги отправляет админ
    // руками; система гасит долг сама по факту поступления.
    const [pending, setPending] = useState<TeamWithdrawalPendingItem[]>([]);
    const [pendingError, setPendingError] = useState<unknown>(null);
    const [pendingBusy, setPendingBusy] = useState("");
    const [payoutTeam, setPayoutTeam] = useState<TeamItem | null>(null);
    const [payoutAddresses, setPayoutAddresses] = useState<TeamPayoutAddress[]>([]);
    const [payoutInput, setPayoutInput] = useState("");
    const [payoutLoading, setPayoutLoading] = useState(false);
    const [payoutSaving, setPayoutSaving] = useState(false);
    const [payoutError, setPayoutError] = useState<unknown>(null);

    // Корректировка баланса команды (EXT-50): беззнаковая сумма, направление
    // типом, ключ идемпотентности.
    //
    // Ключ форма генерирует сама и не показывает. У мерчанта поле для него есть,
    // и там оно заслужено: сверки долгового кабинета печатают готовый ключ в
    // тексте алерта, и проводка именно с этим ключом гасит алерт. На балансе
    // КОМАНДЫ такого случая нет — ListMerchantDebtCorrections читает коррекции
    // только с баланса мерчанта, поэтому набранный здесь ключ не прочитала бы
    // ни одна сверка. Осталась бы графа, которую нечем осмысленно заполнить.
    const [correctionTeam, setCorrectionTeam] = useState<TeamItem | null>(null);
    const [correctionType, setCorrectionType] = useState<TeamBalanceOperationType>("withdrawal");
    const [correctionAmount, setCorrectionAmount] = useState("");
    const [correctionKey, setCorrectionKey] = useState("");
    const [correctionMerchantId, setCorrectionMerchantId] = useState("");
    const [correctionSaving, setCorrectionSaving] = useState(false);
    const [correctionError, setCorrectionError] = useState<unknown>(null);
    const [merchants, setMerchants] = useState<MerchantResponse[]>([]);

    const [resetTeam, setResetTeam] = useState<TeamItem | null>(null);
    const [resetSaving, setResetSaving] = useState(false);
    const [resetError, setResetError] = useState<unknown>(null);

    const reloadPending = useCallback(async (): Promise<void> => {
        if (!token) return;
        try {
            const resp = await listPendingTeamWithdrawals(token);
            setPending(resp.items);
            setPendingError(null);
        } catch (err) {
            setPending([]);
            setPendingError(formatTeamWithdrawalError(err));
        }
    }, [token]);

    const reload = useCallback((): void => {
        if (!token) return;
        setLoading(true);
        setError(null);
        Promise.all([
            listTeams(token, {limit: LIST_LIMIT}),
            listTraders(token, {limit: LIST_LIMIT}),
        ])
            .then(([teamResp, traderResp]) => {
                setTeams(teamResp.items);
                setTraders(traderResp.items);
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
        void reloadPending();
    }, [reloadPending, token]);

    useEffect(() => {
        reload();
    }, [reload]);

    const filteredTeams = useMemo(
        () => teams.filter((team) => matchesTeam(team, teamQuery)),
        [teams, teamQuery],
    );
    const filteredTraders = useMemo(
        () => traders.filter((trader) => matchesTrader(trader, traderQuery)),
        [traders, traderQuery],
    );

    const openTeamModal = (): void => {
        setTeamName("");
        setTeamError(null);
        setTeamOpen(true);
    };

    const openPayoutModal = (team: TeamItem): void => {
        setPayoutTeam(team);
        setPayoutAddresses([]);
        setPayoutInput("");
        setPayoutError(null);
        if (!token) return;
        setPayoutLoading(true);
        listTeamPayoutAddresses(token, team.id)
            .then((addresses) => setPayoutAddresses(addresses))
            .catch((err) => setPayoutError(formatTeamWithdrawalError(err)))
            .finally(() => setPayoutLoading(false));
    };

    const closePayoutModal = (): void => {
        if (payoutSaving) return;
        setPayoutTeam(null);
        setPayoutError(null);
    };

    const openCorrectionModal = (team: TeamItem): void => {
        setCorrectionTeam(team);
        setCorrectionType("withdrawal");
        setCorrectionAmount("");
        setCorrectionKey(newOperationKey());
        setCorrectionMerchantId("");
        setCorrectionError(null);
        if (token && merchants.length === 0) {
            listMerchants(token, {limit: LIST_LIMIT})
                .then((resp) => setMerchants(resp.items))
                // Без списка мерчантов форма остаётся рабочей — просто без
                // контрагента; сам запрос корректировки от этого не зависит.
                .catch(() => setMerchants([]));
        }
    };

    const closeCorrectionModal = (): void => {
        if (correctionSaving) return;
        setCorrectionTeam(null);
        setCorrectionError(null);
    };

    const submitCorrection = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!token || !correctionTeam) return;
        const amount = correctionAmount.trim();
        const problem = validateUsdtAmount(amount);
        if (problem !== null) {
            setCorrectionError(new Error(`Сумма: ${problem}.`));
            return;
        }
        if (!isPositiveAmountString(amount)) {
            setCorrectionError(new Error("Сумма: должна быть больше нуля."));
            return;
        }
        setCorrectionError(null);
        setCorrectionSaving(true);
        applyTeamBalanceOperation(token, correctionTeam.id, {
            type: correctionType,
            amount,
            operationKey: correctionKey,
            ...(correctionMerchantId ? {merchantId: correctionMerchantId} : {}),
        })
            .then((resp) => {
                setSuccess(
                    `${correctionType === "topup" ? "Пополнение" : "Списание"} ${resp.operation.amount} ${resp.currency} по команде «${correctionTeam.name}» проведено. Баланс: ${resp.balance} ${resp.currency}.`,
                );
                setCorrectionTeam(null);
                reload();
            })
            .catch((err) => setCorrectionError(err))
            .finally(() => setCorrectionSaving(false));
    };

    const submitReset = (): void => {
        if (!token || !resetTeam) return;
        setResetError(null);
        setResetSaving(true);
        resetTeamBalanceAsAdmin(token, resetTeam.id)
            .then(() => {
                setSuccess(`Баланс команды «${resetTeam.name}» обнулён.`);
                setResetTeam(null);
                reload();
            })
            .catch((err) => setResetError(err))
            .finally(() => setResetSaving(false));
    };

    const submitPayoutAddress = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!token || !payoutTeam) return;
        const address = payoutInput.trim();
        if (!address) {
            setPayoutError("Введите TRON-адрес вывода.");
            return;
        }
        setPayoutSaving(true);
        setPayoutError(null);
        addTeamPayoutAddress(token, payoutTeam.id, address)
            .then((bound) => {
                // Новый кошелёк — сверху, как его отдаёт сервер; повторная
                // привязка того же адреса второй строки не даёт.
                setPayoutAddresses((prev) => [bound, ...prev.filter((item) => item.id !== bound.id)]);
                setPayoutInput("");
                setSuccess(`Адрес вывода привязан к команде ${payoutTeam.name}.`);
            })
            .catch((err) => setPayoutError(formatTeamWithdrawalError(err)))
            .finally(() => setPayoutSaving(false));
    };

    const disablePayoutAddress = (address: TeamPayoutAddress): void => {
        if (!token || !payoutTeam) return;
        setPayoutSaving(true);
        setPayoutError(null);
        disableTeamPayoutAddress(token, payoutTeam.id, address.id)
            .then(() => {
                setPayoutAddresses((prev) => prev.filter((item) => item.id !== address.id));
                setSuccess(`Адрес вывода команды ${payoutTeam.name} отвязан.`);
            })
            .catch((err) => setPayoutError(formatTeamWithdrawalError(err)))
            .finally(() => setPayoutSaving(false));
    };

    const finishWithdrawal = (item: TeamWithdrawalPendingItem, action: "complete" | "cancel"): void => {
        if (!token || pendingBusy) return;
        setPendingBusy(item.id);
        setPendingError(null);
        const run = action === "complete"
            ? completeTeamWithdrawal(token, item.id)
            : cancelTeamWithdrawal(token, item.id);
        run
            .then(async () => {
                setSuccess(action === "complete"
                    ? `Запрос вывода команды ${item.teamName} завершён вручную.`
                    : `Запрос вывода команды ${item.teamName} отменён.`);
                await reloadPending();
                const teamResp = await listTeams(token, {limit: LIST_LIMIT});
                setTeams(teamResp.items);
            })
            .catch((err) => setPendingError(formatTeamWithdrawalError(err)))
            .finally(() => setPendingBusy(""));
    };

    const openTraderModal = (): void => {
        setTraderLogin("");
        setTraderPassword("");
        setTraderName("");
        setTraderTeamId(teams[0]?.id || "");
        setTraderError(null);
        setTraderOpen(true);
    };

    const submitTeam = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!token) {
            setTeamError("Требуется повторный вход.");
            return;
        }
        const name = teamName.trim();
        if (!name) {
            setTeamError("Введите название команды.");
            return;
        }
        if (name.length > TEAM_NAME_MAX) {
            setTeamError(`Название команды: не больше ${TEAM_NAME_MAX} символов.`);
            return;
        }

        setTeamSaving(true);
        setTeamError(null);
        createTeam(token, name)
            .then(async (team) => {
                setTeamOpen(false);
                setSuccess(`Команда создана: ${team.name}.`);
                const resp = await listTeams(token, {limit: LIST_LIMIT});
                setTeams(resp.items);
            })
            .catch((err) => setTeamError(err))
            .finally(() => setTeamSaving(false));
    };

    const submitTrader = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!token) {
            setTraderError("Требуется повторный вход.");
            return;
        }
        const login = traderLogin.trim();
        const password = traderPassword.trim();
        const name = traderName.trim();
        const teamId = traderTeamId.trim();
        if (!login || !password || !name || !teamId) {
            setTraderError("Заполните логин, пароль, имя и команду трейдера.");
            return;
        }
        // Та же политика, что и у кабинета мерчанта: бэкенд гоняет sign-up
        // трейдера через неё же. Проверяем здесь, чтобы админ увидел конкретное
        // правило до отправки формы, а не после отказа сервера.
        const policyError = validateCredentialLogin(login) ?? validateCredentialPassword(password);
        if (policyError) {
            setTraderError(policyError);
            return;
        }

        setTraderSaving(true);
        setTraderError(null);
        adminSignUp(token, {
            login,
            password,
            name,
            type: "trader",
            roles: ["trader"],
            isActive: true,
            teamId,
        })
            .then(async ({referenceId}) => {
                setTraderOpen(false);
                setTraderPassword("");
                setTraderPasswordVisible(false);
                setSuccess(`Трейдер создан: ${shortId(referenceId)}.`);
                const resp = await listTraders(token, {limit: LIST_LIMIT});
                setTraders(resp.items);
            })
            .catch((err) => setTraderError(err))
            .finally(() => setTraderSaving(false));
    };

    return (
        <div className="page admin-traders">
            <div className="page-header">
                <div>
                    <h2>Traders</h2>
                    <p className="page-subtitle">{traders.length} traders · {teams.length} teams</p>
                </div>
                <div className="page-actions">
                    <button className="btn btn-ghost btn-sm" type="button" onClick={reload} disabled={loading}>
                        {loading ? "Обновление…" : "Обновить"}
                    </button>
                    <button className="btn btn-ghost btn-sm" type="button" onClick={openTeamModal}>
                        Добавить команду
                    </button>
                    <button className="btn btn-primary btn-sm" type="button" onClick={openTraderModal}>
                        Добавить трейдера
                    </button>
                </div>
            </div>

            <WithdrawalMinimum audience="trader" editable/>

            {success && (
                <div className="alert alert-info">
                    {success}
                    <button className="alert-dismiss" type="button" onClick={() => setSuccess("")}>
                        ×
                    </button>
                </div>
            )}
            <ErrorBanner error={error} title="Списки трейдеров и команд не загружены" />

            <div className="admin-split-grid">
                <section className="admin-list-section">
                    <div className="section-header">
                        <div>
                            <h3>Трейдеры</h3>
                            <span>{filteredTraders.length} rows</span>
                        </div>
                        <input
                            type="search"
                            value={traderQuery}
                            onChange={(e) => setTraderQuery(e.target.value)}
                            placeholder="Фильтр по логину, имени, команде"
                            autoComplete="off"
                        />
                    </div>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Login</th>
                                    <th>Name</th>
                                    <th>Team</th>
                                    <th>Status</th>
                                    <th>2FA</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="empty-state">Загрузка трейдеров…</td>
                                    </tr>
                                ) : filteredTraders.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="empty-state">Трейдеры не найдены.</td>
                                    </tr>
                                ) : (
                                    filteredTraders.map((trader) => (
                                        <tr key={trader.id}>
                                            <td><CopyableId id={trader.id}/></td>
                                            <td><strong>{trader.login}</strong></td>
                                            <td>{trader.name}</td>
                                            <td>
                                                {trader.teamName ? (
                                                    <>
                                                        <strong>{trader.teamName}</strong>
                                                        {trader.teamId && <div className="cell-subtle"><CopyableId id={trader.teamId}/></div>}
                                                    </>
                                                ) : (
                                                    <span className="cell-subtle">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`status-dot ${trader.isActive ? "dot-active" : "dot-inactive"}`} />
                                                {trader.isActive ? "Active" : "Inactive"}
                                            </td>
                                            <td>
                                                <SecondFactorControl
                                                    userId={trader.userId}
                                                    login={trader.login}
                                                    status={trader.secondFactor}
                                                    onChanged={(secondFactor) =>
                                                        setTraders((items) =>
                                                            items.map((item) =>
                                                                item.id === trader.id ? {...item, secondFactor} : item,
                                                            ),
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td className="mono">{new Date(trader.createdAt).toLocaleString()}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="admin-list-section">
                    <div className="section-header">
                        <div>
                            <h3>Команды</h3>
                            <span>{filteredTeams.length} rows</span>
                        </div>
                        <input
                            type="search"
                            value={teamQuery}
                            onChange={(e) => setTeamQuery(e.target.value)}
                            placeholder="Фильтр по имени или ID"
                            autoComplete="off"
                        />
                    </div>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Balance</th>
                                    <th>Created</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="empty-state">Загрузка команд…</td>
                                    </tr>
                                ) : filteredTeams.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="empty-state">Команды не найдены.</td>
                                    </tr>
                                ) : (
                                    filteredTeams.map((team) => (
                                        <tr key={team.id}>
                                            <td><CopyableId id={team.id}/></td>
                                            <td><strong>{team.name}</strong></td>
                                            <td>
                                                <strong>{team.amount} {team.currency}</strong>
                                                <div className="cell-subtle"><CopyableId id={team.balanceId}/></div>
                                            </td>
                                            <td className="mono">{new Date(team.createdAt).toLocaleString()}</td>
                                            <td className="team-action-cell">
                                                <div className="team-action-buttons">
                                                    <button
                                                        className="btn btn-ghost btn-sm"
                                                        type="button"
                                                        onClick={() => openPayoutModal(team)}
                                                    >
                                                        Адреса вывода
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-sm"
                                                        type="button"
                                                        onClick={() => openCorrectionModal(team)}
                                                    >
                                                        Корректировка
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        type="button"
                                                        onClick={() => {
                                                            setResetError(null);
                                                            setResetTeam(team);
                                                        }}
                                                    >
                                                        Обнулить
                                                    </button>
                                                </div>
                                                {pending.some((item) => item.teamId === team.id) && (
                                                    <div className="cell-subtle">запрос активен</div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <section className="admin-list-section">
                <div className="section-header">
                    <div>
                        <h3>Запросы вывода</h3>
                        <span>{pending.length} активных</span>
                    </div>
                </div>
                <ErrorBanner error={pendingError} title="Запросы вывода не загружены" />
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Команда</th>
                                <th>Сумма</th>
                                <th>Поступило</th>
                                <th>Адрес</th>
                                <th>Запрошен</th>
                                <th>Действие</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pending.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="empty-state">Активных запросов вывода нет.</td>
                                </tr>
                            ) : (
                                pending.map((item) => (
                                    <tr key={item.id}>
                                        <td>
                                            <strong>{item.teamName}</strong>
                                            <div className="cell-subtle mono">{shortId(item.teamId)}</div>
                                        </td>
                                        <td className="mono">{item.amount} {item.currency}</td>
                                        <td className="mono">{item.received} {item.currency}</td>
                                        <td className="mono" title={item.address}>{item.address}</td>
                                        <td className="mono">{new Date(item.createdAt).toLocaleString()}</td>
                                        <td>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                type="button"
                                                disabled={pendingBusy === item.id}
                                                onClick={() => finishWithdrawal(item, "complete")}
                                            >
                                                Завершить
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                type="button"
                                                disabled={pendingBusy === item.id}
                                                onClick={() => finishWithdrawal(item, "cancel")}
                                            >
                                                Отменить
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <AdminSecondFactorSection />

            <Modal
                open={payoutTeam !== null}
                title={payoutTeam ? `Адреса вывода: ${payoutTeam.name}` : "Адреса вывода"}
                onClose={closePayoutModal}
                disableClose={payoutSaving}
                sizeClassName="modal-sm"
            >
                <p className="modal-hint">
                    У команды может быть несколько кошельков. Трейдер при запросе выбирает, на какой ждать
                    деньги, но долг гасит поступление на любой адрес команды. Адрес, на который висит
                    активный запрос, не отвязывается.
                </p>
                <ErrorBanner error={payoutError} title="Адреса вывода не изменены" />
                {payoutLoading ? (
                    <div className="empty-state compact">Загрузка адресов…</div>
                ) : payoutAddresses.length === 0 ? (
                    <div className="empty-state compact">Адреса вывода не привязаны.</div>
                ) : (
                    <div className="confirm-summary payout-address-list">
                        {payoutAddresses.map((item) => (
                            <div key={item.id}>
                                <span>
                                    <strong className="mono" title={item.address}>{item.address}</strong>
                                    <small className="cell-subtle">
                                        {item.network.toUpperCase()}, с {new Date(item.activeFrom).toLocaleString()}
                                    </small>
                                </span>
                                <button
                                    className="btn btn-danger btn-sm"
                                    type="button"
                                    onClick={() => disablePayoutAddress(item)}
                                    disabled={payoutSaving}
                                >
                                    Отвязать
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <form className="onboarding-form" onSubmit={submitPayoutAddress}>
                    <div className="form-group">
                        <label htmlFor="team-payout-address">Добавить TRON-адрес (TRC20)</label>
                        <input
                            id="team-payout-address"
                            type="text"
                            value={payoutInput}
                            onChange={(e) => setPayoutInput(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <small>Адрес не может совпадать с депозитным адресом мерчанта или адресом другой команды.</small>
                    </div>
                    <div className="modal-actions">
                        <button className="btn btn-ghost" type="button" onClick={closePayoutModal} disabled={payoutSaving}>
                            Закрыть
                        </button>
                        <button className="btn btn-primary" type="submit" disabled={payoutSaving}>
                            {payoutSaving ? "Привязка…" : "Привязать"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={correctionTeam !== null}
                title={correctionTeam ? `Корректировка: ${correctionTeam.name}` : "Корректировка"}
                onClose={closeCorrectionModal}
                sizeClassName="modal-sm"
            >
                <form className="onboarding-form" onSubmit={submitCorrection}>
                    <ErrorBanner error={correctionError} title="Корректировка не проведена" />
                    <p className="modal-hint">
                        Ручное движение денег по балансу команды: например команда получила
                        выплату мимо нас и её баланс завышен на эту сумму.
                    </p>
                    {correctionTeam && (
                        <p className="modal-hint">
                            Баланс сейчас: <strong>{correctionTeam.amount} {correctionTeam.currency}</strong>
                        </p>
                    )}
                    <div className="form-group">
                        <label htmlFor="team-correction-type">Тип</label>
                        <select
                            id="team-correction-type"
                            value={correctionType}
                            onChange={(e) => setCorrectionType(e.target.value as TeamBalanceOperationType)}
                        >
                            <option value="withdrawal">Списание (команда получила деньги вне системы)</option>
                            <option value="topup">Пополнение</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="team-correction-amount">Сумма, USDT</label>
                        <input
                            id="team-correction-amount"
                            type="text"
                            inputMode="decimal"
                            value={correctionAmount}
                            onChange={(e) => setCorrectionAmount(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <small>Без знака: направление задаёт тип операции.</small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="team-correction-merchant">Контрагент-мерчант</label>
                        <select
                            id="team-correction-merchant"
                            value={correctionMerchantId}
                            onChange={(e) => setCorrectionMerchantId(e.target.value)}
                        >
                            <option value="">— без атрибуции —</option>
                            {merchants.map((merchant) => (
                                <option key={merchant.id} value={merchant.id}>
                                    {merchant.name}
                                </option>
                            ))}
                        </select>
                        <small>
                            Чьи это деньги: корректировка по кабинету обязана лечь в срез его
                            мерчанта, иначе разъедется выводимый остаток.
                        </small>
                    </div>
                    <div className="modal-actions">
                        <button className="btn btn-ghost" type="button" onClick={closeCorrectionModal} disabled={correctionSaving}>
                            Отмена
                        </button>
                        <button className="btn btn-primary" type="submit" disabled={correctionSaving}>
                            {correctionSaving ? "Проведение…" : "Провести"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={resetTeam !== null}
                title={resetTeam ? `Обнулить баланс: ${resetTeam.name}` : "Обнулить баланс"}
                onClose={() => {
                    if (!resetSaving) setResetTeam(null);
                }}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={resetError} title="Баланс не обнулён" />
                <p className="modal-hint">
                    Баланс команды {resetTeam ? <strong>{resetTeam.amount} {resetTeam.currency}</strong> : null} будет
                    списан в ноль померчантными проводками team_reset. Действие необратимо.
                </p>
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setResetTeam(null)}
                        disabled={resetSaving}
                    >
                        Отмена
                    </button>
                    <button className="btn btn-danger" type="button" onClick={submitReset} disabled={resetSaving}>
                        {resetSaving ? "Обнуление…" : "Обнулить"}
                    </button>
                </div>
            </Modal>

            <Modal open={teamOpen} title="Добавить команду" onClose={() => setTeamOpen(false)} sizeClassName="modal-sm">
                <form className="onboarding-form" onSubmit={submitTeam}>
                    <ErrorBanner error={teamError} title="Команда не создана" />
                    <div className="form-group">
                        <label htmlFor="team-name">Название команды</label>
                        <input
                            id="team-name"
                            type="text"
                            maxLength={TEAM_NAME_MAX}
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    <div className="modal-actions">
                        <button className="btn btn-ghost" type="button" onClick={() => setTeamOpen(false)} disabled={teamSaving}>
                            Отмена
                        </button>
                        <button className="btn btn-primary" type="submit" disabled={teamSaving}>
                            {teamSaving ? "Создание…" : "Создать"}
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal open={traderOpen} title="Добавить трейдера" onClose={() => setTraderOpen(false)} sizeClassName="modal-sm">
                <form className="onboarding-form" onSubmit={submitTrader}>
                    <ErrorBanner error={traderError} title="Трейдер не создан" />
                    <div className="form-group">
                        <label htmlFor="trader-login">Логин</label>
                        <input
                            id="trader-login"
                            type="text"
                            value={traderLogin}
                            onChange={(e) => setTraderLogin(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <small>3–100 символов: латиница, цифры, точка, дефис или подчёркивание.</small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="trader-password">Пароль</label>
                        <input
                            id="trader-password"
                            type={traderPasswordVisible ? "text" : "password"}
                            value={traderPassword}
                            onChange={(e) => setTraderPassword(e.target.value)}
                            autoComplete="new-password"
                        />
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={() => {
                                setTraderPassword(generateMerchantPortalPassword());
                                setTraderPasswordVisible(true);
                            }}
                        >
                            Сгенерировать безопасный пароль
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            aria-controls="trader-password"
                            aria-pressed={traderPasswordVisible}
                            onClick={() => setTraderPasswordVisible((visible) => !visible)}
                        >
                            {traderPasswordVisible ? "Скрыть пароль" : "Показать пароль"}
                        </button>
                        <small>Не менее 15 символов; максимум 72 байта.</small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="trader-name">Name</label>
                        <input
                            id="trader-name"
                            type="text"
                            value={traderName}
                            onChange={(e) => setTraderName(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="trader-team">Команда</label>
                        <select
                            id="trader-team"
                            value={traderTeamId}
                            onChange={(e) => setTraderTeamId(e.target.value)}
                        >
                            <option value="">Выберите команду</option>
                            {teams.map((team) => (
                                <option key={team.id} value={team.id}>
                                    {team.name} · {shortId(team.id)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="modal-actions">
                        <button className="btn btn-ghost" type="button" onClick={() => setTraderOpen(false)} disabled={traderSaving}>
                            Отмена
                        </button>
                        <button className="btn btn-primary" type="submit" disabled={traderSaving}>
                            {traderSaving ? "Создание…" : "Создать"}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
